import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "../utils/i18n";
import {
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  detectBrowserLanguage,
  LanguagePreference,
} from "../utils/languageRegistry";

export interface LanguageSelectorProps {
  variant?: "dropdown" | "pills" | "buttons";
  className?: string;
  idPrefix?: string;
}

export function LanguageSelector({
  variant = "dropdown",
  className = "",
  idPrefix = "language-selector",
}: LanguageSelectorProps) {
  const { languagePreference, changeLanguage, locale, t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto resolved display label
  const resolvedBrowserLang = detectBrowserLanguage();
  const resolvedLangObj = getLanguageByCode(resolvedBrowserLang);
  const autoDisplayLabel = `Auto (${resolvedLangObj.nativeName})`;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const options: { id: LanguagePreference; codeLabel: string; name: string; nativeName: string }[] = [
    {
      id: "auto",
      codeLabel: "AUTO",
      name: "Auto",
      nativeName: autoDisplayLabel,
    },
    ...SUPPORTED_LANGUAGES.map((lang) => ({
      id: lang.code as LanguagePreference,
      codeLabel: lang.code.toUpperCase(),
      name: lang.name,
      nativeName: lang.nativeName,
    })),
  ];

  const currentOption = options.find((o) => o.id === languagePreference) || options[0];

  if (variant === "dropdown") {
    return (
      <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
        <button
          type="button"
          id={`${idPrefix}-dropdown-btn`}
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`Current language: ${currentOption.nativeName}. Click to change language.`}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700/80 hover:bg-slate-200/80 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        >
          <Globe className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 shrink-0" />
          <span className="capitalize">{currentOption.id === "auto" ? "Auto" : currentOption.codeLabel}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1.5 w-48 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl ring-1 ring-black/5 dark:ring-white/10 z-50 p-1 space-y-0.5 font-sans">
            <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800/80 mb-0.5">
              {t("settings_language") || "Select Language"}
            </div>
            {options.map((option) => {
              const isSelected = languagePreference === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  id={`${idPrefix}-opt-${option.id}`}
                  onClick={() => {
                    changeLanguage(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs font-medium rounded-lg transition cursor-pointer ${
                    isSelected
                      ? "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-[10px] font-bold tracking-wide w-8 text-left text-slate-400 dark:text-slate-500">
                      {option.codeLabel}
                    </span>
                    <span className="truncate">{option.nativeName}</span>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Variant: Pills / Buttons for Settings panel
  return (
    <div className={`flex flex-wrap gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 rounded-xl ${className}`}>
      {options.map((option) => {
        const isSelected = languagePreference === option.id;
        return (
          <button
            key={option.id}
            type="button"
            id={`${idPrefix}-pill-${option.id}`}
            onClick={() => changeLanguage(option.id)}
            className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition cursor-pointer ${
              isSelected
                ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 font-bold shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
            }`}
          >
            <span className="truncate">{option.nativeName}</span>
          </button>
        );
      })}
    </div>
  );
}
